import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Alert, HeroCard, Link, Separator, Text } from '@/ui'
import { ArticleCard } from '../shared/ArticleCard'
import {
  ARTICLE_TYPE,
  FinancialInsightsButtonType,
  INSIGHTS_TYPE,
} from '../shared/utils'
import { fetchFinancialAccountsIfNeeded } from '@/actions/financialAccountActions'
import {
  useReselector,
  useToggle,
  useTrackFinancialInsightsButtonClick,
} from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'

import { InsightsBreakdownTable } from '../shared/InsightsBreakdownTable'
import { CurrentPanel } from '../shared/CurrentPanel'
import { UncategorizedTransactionBanner } from '../shared/UncategorizedTransactionBanner'
import { isEmpty } from 'lodash'
import { selectActivePlaidFinancialAccounts } from '@/selectors/financeSelectors'
import { TrackInsightsSection } from '../shared/TrackInsightsSection'
import { fetchInsightsTableDropdownOptions } from '../shared/financeActions'
import {
  InsightsDatePicker,
  useInsightsDatePicker,
} from '../shared/InsightsDatePicker'
import { InsightBreadcrumb } from '../shared/InsightBreadcrumb'
import { ImageBackgroundColors, TypographyStyles } from '@/styles'

export const InsightsIncomePanel = () => {
  const dispatch = useAppDispatch()
  const [displayBanner, toggleBanner] = useToggle(true)
  const [years, setYears] = useState<number[]>([])

  const [searchParams] = useSearchParams()

  const { monthParam, yearParam } = useMemo(() => {
    return {
      monthParam: searchParams.get('month'),
      yearParam: searchParams.get('year'),
    }
  }, [searchParams])

  const { yearFilter, monthFilter, inputDate, setYearFilter, setMonthFilter } =
    useInsightsDatePicker(monthParam, yearParam)

  const trackInsights = useTrackFinancialInsightsButtonClick()

  useEffect(() => {
    dispatch(fetchFinancialAccountsIfNeeded())
  }, [dispatch])

  useEffect(() => {
    const fetchYears = async () => {
      const years = await fetchInsightsTableDropdownOptions(
        INSIGHTS_TYPE.INCOME
      )(dispatch)

      if (years?.length) {
        setYears(years)
      } else {
        setYears([])
      }
    }
    fetchYears()
  }, [dispatch])

  const linkedPlaidAccounts = useReselector(selectActivePlaidFinancialAccounts)
  const hasLinkedPlaidAccounts = !isEmpty(linkedPlaidAccounts)

  const closeBanner = () => {
    toggleBanner()
    trackInsights({
      button: FinancialInsightsButtonType.CLOSE_BANNER,
      insightsType: INSIGHTS_TYPE.INCOME,
    })
  }

  return (
    <div className="space-y-6">
      <InsightBreadcrumb month={monthFilter} year={yearFilter} />

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <Text variant={TypographyStyles.H1}>Income</Text>
        <InsightsDatePicker
          monthFilter={monthFilter}
          yearFilter={yearFilter}
          handleMonthFilterChange={setMonthFilter}
          handleYearFilterChange={setYearFilter}
          insightsType={INSIGHTS_TYPE.INCOME}
        />
      </div>

      {displayBanner && (
        <HeroCard
          title="What is Income?"
          imageSrc="https://heard-images.s3.amazonaws.com/assets/pavilion.svg"
          imageAlt="What is Income?"
          imageColor={ImageBackgroundColors.YELLOW}
          onClose={closeBanner}
        >
          <Text variant={TypographyStyles.BODY_SM}>
            Income is the money you receive in exchange for your services. If
            you have other streams of income deposited into the linked accounts,
            you can see it all here in one place.
          </Text>
        </HeroCard>
      )}

      {!hasLinkedPlaidAccounts && (
        <Alert>
          <Text variant={TypographyStyles.BODY_SM}>
            You do not have a connected bank account.{' '}
            <Link href="/accounts#connected-institutions" newPage={false}>
              Link Business Account
            </Link>
          </Text>
        </Alert>
      )}

      <UncategorizedTransactionBanner inputDate={inputDate} />
      <CurrentPanel insightsType={INSIGHTS_TYPE.INCOME} inputDate={inputDate} />
      <br />

      <TrackInsightsSection type={INSIGHTS_TYPE.INCOME} inputDate={inputDate} />

      <br />

      <Text variant={TypographyStyles.H2} className="mt-6 mb-2">
        Income Breakdown
      </Text>
      <InsightsBreakdownTable
        insightsType={INSIGHTS_TYPE.INCOME}
        inputDate={inputDate}
        years={years}
      />

      <Separator />

      <Text variant={TypographyStyles.H2} className="mt-6 mb-2">
        Learn More
      </Text>

      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="w-full sm:w-1/3">
          <ArticleCard
            title="The Complete List of Income Streams for Therapists"
            type={ARTICLE_TYPE.ARTICLE}
            url="https://www.joinheard.com/articles/the-complete-list-of-income-streams-for-therapists"
            insightsType={INSIGHTS_TYPE.INCOME}
          />
        </div>
        <div className="w-full sm:w-1/3">
          <ArticleCard
            title="How to Set Your Fees in Private Practice (Plus 3 Mistakes to Avoid)"
            type={ARTICLE_TYPE.ARTICLE}
            url="https://www.joinheard.com/articles/how-to-set-your-fees-in-private-practice-plus-3-mistakes-to-avoid"
            insightsType={INSIGHTS_TYPE.INCOME}
          />
        </div>
        <div className="w-full sm:w-1/3">
          <ArticleCard
            title="Should Therapists Have a Separate Business for Other Income Streams?"
            type={ARTICLE_TYPE.ARTICLE}
            url="https://www.joinheard.com/articles/should-therapists-have-a-separate-business-for-other-income-streams"
            insightsType={INSIGHTS_TYPE.INCOME}
          />
        </div>
      </div>
    </div>
  )
}
